import React, { useEffect, useState, type ReactNode } from "react";
import { Link } from 'react-router-dom';
import { ArrowLeft, Building2, ChevronDown, ShieldCheck } from 'lucide-react';
import './RestaurantAccess.css';
import { get } from "../../services/api";
import { getSelectedRestaurant, selectRestaurant } from "../utils/restaurantContext";

type Restaurant = { id: number; name: string };

export default function RestaurantAccess({ children }: { children: ReactNode }) {
  const [restaurants, setRestaurants] = useState<Restaurant[]>([]);
  const [selected, setSelected] = useState(getSelectedRestaurant() ?? "");
  const [ready, setReady] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    get<Restaurant[]>("/api/restaurants").then((items) => {
      if (!active) return;
      setRestaurants(items);
      if (!items.some((item) => String(item.id) === selected)) setSelected("");
      setReady(true);
    }).catch((err: Error) => {
      if (active) setError(err.message);
    });
    return () => { active = false; };
    // Validate the saved selection once when this workspace opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const currentRestaurant = restaurants.find((restaurant) => String(restaurant.id) === selected);

  return <div className="restaurant-access">
    <header className="restaurant-toolbar" aria-label="Restaurant workspace">
      <Link to="/super-admin" className="restaurant-toolbar-back"><ArrowLeft size={17} aria-hidden="true" /><span>Back to platform</span></Link>
      <div className="restaurant-toolbar-context">
        <span className="restaurant-toolbar-icon"><Building2 size={22} aria-hidden="true" /></span>
        <div><span className="restaurant-toolbar-eyebrow">RESTAURANT WORKSPACE</span><p className="restaurant-toolbar-name">{currentRestaurant?.name || 'Choose your restaurant'}</p></div>
        <span className="restaurant-toolbar-badge"><ShieldCheck size={14} aria-hidden="true" />Super Admin</span>
      </div>
      <div className="restaurant-toolbar-switcher">
        <label htmlFor="restaurant-workspace">Switch restaurant</label>
        <div className="restaurant-toolbar-select">
          <select id="restaurant-workspace" value={selected} disabled={!ready || !restaurants.length} onChange={(event) => {
            selectRestaurant(event.target.value);
            setSelected(event.target.value);
          }}>
            <option value="" disabled>Select a restaurant</option>
            {restaurants.map((restaurant) => <option key={restaurant.id} value={restaurant.id}>{restaurant.name}</option>)}
          </select>
          <ChevronDown size={16} aria-hidden="true" />
        </div>
      </div>
    </header>
    <div className="restaurant-toolbar-messages">
      {error && <p role="alert">{error}</p>}
      {!ready && !error && <p>Loading restaurants…</p>}
      {ready && !restaurants.length && <p>No restaurants have been created yet.</p>}
    </div>
    {ready && selected && <React.Fragment key={selected}>{children}</React.Fragment>}
  </div>;
}
